/**
 * Chat Folders Configuration System
 * 
 * This utility provides a flexible system to configure chat folders based on various criteria.
 * You can easily add new folders by adding configuration objects to the DEFAULT_FOLDER_CONFIGS array.
 */

import { i18n } from '@/i18n';


/**
 * Id of the "all channels" row: it is not a folder, it is the absence of a channel
 * scope. Selecting it clears the scope and leaves the group filter untouched.
 */
export const CHANNEL_SCOPE_ALL_ID = 'channel-all';

/**
 * API request builders for different folder types
 */
export const ApiRequestBuilders = {
    /**
     * General folder - all conversations except marketing tagged ones
     */
    general: (store) => {
        const allTags = store?.getters['tags/allTags'] || [];
        const marketingTag = allTags.find(tag => tag.name && tag.name.toLowerCase() === 'marketing');
        
        if (marketingTag) {
            // Exclude conversations with marketing tag
            return {
                filters: { excludedTags: [marketingTag.uuid] },
                params: { 'filters.excludedTag': marketingTag.uuid }
            };
        }
        
        // If no marketing tag found, show all conversations
        return {
            filters: null,
            params: {}
        };
    },

    /**
     * Marketing folder - filter by marketing tag UUID
     */
    marketing: (store) => {
        const allTags = store?.getters['tags/allTags'] || [];
        const marketingTag = allTags.find(tag => tag.name && tag.name.toLowerCase() === 'marketing');
        
        return {
            filters: marketingTag ? { tag: [marketingTag.uuid] } : null,
            params: marketingTag ? { 'filters.tag': marketingTag.uuid } : {}
        };
    },

    /**
     * Escalated to me folder - filter by current user
     */
    escalatedToMe: (store) => {
        const currentUserId = store?.getters['user/user']?.uuid || store?.state?.user?.user?.uuid;
        
        return {
            filters: currentUserId ? { escalatedTo: [currentUserId] } : null,
            params: currentUserId ? { 'filters.escalatedTo': currentUserId } : {}
        };
    },

    /**
     * Unread folder - conversations nobody has opened yet.
     * The value travels as a string because it ends up in the activeFilters store,
     * and the chips render it with String#toLowerCase (ActiveFilters#transformFilter).
     */
    unread: () => ({
        filters: { read: 'false' },
        params: { 'filters.read': 'false' }
    }),

    /**
     * Human agents folder - filter by human agent conversations
     */
    humanAgents: () => ({
        filters: { agent: ['HUMAN_AGENT'] },
        params: { 'filters.agent': 'HUMAN_AGENT' }
    }),

    /**
     * Archived folder - filter by archived conversations
     *
    archived: () => ({
        filters: { archived: true },
        params: { 'filters.archived': true }
    }),

    */

    /**
     * Channel type folder - filter by communication channel type (WHATSAPP, FACEBOOK, etc.)
     * @param {string} channelType - The channel type to filter by
     * @returns {Function} Builder function
     */
    channelType: (channelType) => () => ({
        filters: { channel: [channelType] },
        params: { 'filters.channel': channelType }
    }),

    /**
     * Channel phone number folder - filter by specific WhatsApp phone number
     * @param {string} phoneNumber - The phone number to filter by
     * @returns {Function} Builder function
     */
    channelPhoneNumber: (phoneNumber) => () => ({
        filters: { channelPhoneNumber: [phoneNumber] },
        params: { 'filters.channelPhoneNumber': phoneNumber }
    }),

    /**
     * Folder for one specific number of a channel type (e.g. one of several
     * connected WhatsApp numbers). The number alone is not enough as a filter:
     * a voice (PHONE) channel can answer on the same number as a WhatsApp one.
     * @param {string} channelType - The channel type (WHATSAPP, PHONE, ...)
     * @param {string} phoneNumber - The phone number to filter by
     * @returns {Function} Builder function
     */
    channelTypeAndPhoneNumber: (channelType, phoneNumber) => () => ({
        filters: { channel: [channelType], channelPhoneNumber: [phoneNumber] },
        params: { 'filters.channel': channelType, 'filters.channelPhoneNumber': phoneNumber }
    }),

};

/**
 * Default folder configurations
 * 
 * Each folder config should have:
 * - id: unique identifier
 * - title: display name
 * - icon: Material Design icon name (optional)
 * - svgIcon: SVG icon name from your icon system (optional)
 * - apiRequestBuilder: function that returns API request configuration { filters, params }
 * - enabled: boolean to enable/disable the folder
 * - defaultExpanded: whether the folder starts expanded
 * - order: display order (lower numbers appear first)
 */
export const DEFAULT_FOLDER_CONFIGS = [
    {
        id: 'unread-conversations',
        title: i18n.t('chat.folders.unread'),
        description: 'Conversations nobody has opened yet',
        svgIcon: 'notifications_active',
        apiRequestBuilder: ApiRequestBuilders.unread,
        enabled: true,
        defaultExpanded: false,
        order: 2,
        tags: {
            include: [],
            exclude: [],
        },
    },
    {
        id: 'escalated-to-me',
        title: i18n.t('chat.folders.escalatedToMe'),
        description: 'Conversations that have been escalated to you for human intervention',
        svgIcon: 'person',
        apiRequestBuilder: ApiRequestBuilders.escalatedToMe,
        enabled: true,
        defaultExpanded: true,
        order: 3, // Show first - most important
        // Tag metadata for UI: include/exclude tag names
        tags: {
            include: [],
            exclude: [],
        },
    },
    // {
    //     id: 'urgent',
    //     title: 'Urgent',
    //     description: 'High-priority conversations requiring immediate attention',
    //     svgIcon: 'warning',
    //     apiRequestBuilder: (store) => ({
    //         filters: { tag: ['urgent', 'priority'] },
    //         params: { 'filters.tag': 'urgent' }
    //     }),
    //     enabled: true,
    //     defaultExpanded: false,
    //     order: 1 // Show first
    // },
    {
        id: 'marketing',
        title: i18n.t('chat.folders.marketing'),
        description: 'Conversations related to marketing campaigns and promotional activities',
        svgIcon: 'marketing',
        apiRequestBuilder: ApiRequestBuilders.marketing,
        enabled: true,
        defaultExpanded: false,
        order: 4,
        tags: {
            include: ['marketing'],
            exclude: [],
        },
    },
    // {
    //     id: 'support',
    //     title: 'Customer Support',
    //     description: 'Customer service and technical support conversations',
    //     svgIcon: 'SUPPORT_AGENT',
    //     apiRequestBuilder: (store) => ({
    //         filters: { tag: ['support', 'help', 'technical'] },
    //         params: { 'filters.tag': 'support' }
    //     }),
    //     enabled: true,
    //     defaultExpanded: false,
    //     order: 4
    // },
    // {
    //     id: 'sales',
    //     title: 'Sales',
    //     description: 'Sales inquiries, leads, and prospect conversations',
    //     svgIcon: 'SALES_AGENT',
    //     apiRequestBuilder: (store) => ({
    //         filters: { tag: ['sales', 'lead', 'prospect'] },
    //         params: { 'filters.tag': 'sales' }
    //     }),
    //     enabled: true,
    //     defaultExpanded: false,
    //     order: 5
    // },
    {
        id: 'human-agents',
        title: i18n.t('chat.folders.humanAgents'),
        description: 'Conversations handled by human agents and support staff',
        svgIcon: 'people',
        apiRequestBuilder: ApiRequestBuilders.humanAgents,
        enabled: true,
        defaultExpanded: false,
        order: 5,
        tags: {
            include: [],
            exclude: [],
        },
    },
    {
        id: 'general-conversations',
        title: i18n.t('chat.folders.general'),
        description: 'All conversations not categorized in specific folders',
        svgIcon: 'chat_bubble',
        apiRequestBuilder: ApiRequestBuilders.general,
        enabled: true,
        defaultExpanded: false,
        order: 1, // Show first
        tags: {
            include: [],
            exclude: ['marketing'],
        },
    },
    {
        id: 'archived-conversations',
        title: i18n.t('chat.folders.archived'),
        description: 'Archived conversations that have been marked as completed or inactive',
        svgIcon: 'archive',
        apiRequestBuilder: ApiRequestBuilders.archived,
        enabled: true,
        defaultExpanded: false,
        order: 6, // Show last
        tags: {
            include: [],
            exclude: [],
        },
    },
    // {
    //     id: 'ai-conversations',
    //     title: 'AI Conversations',
    //     description: 'Conversations handled by AI agents and automated systems',
    //     svgIcon: 'smart_toy',
    //     apiRequestBuilder: (store) => ({
    //         filters: { agent: ['AI_AGENT', 'GPT_4', 'GPT_3_5'] },
    //         params: { 'filters.agent': 'AI_AGENT' }
    //     }),
    //     enabled: false, // Disabled by default
    //     defaultExpanded: false,
    //     order: 6
    // }
];

/**
 * Chat Folders Manager Class
 * Provides methods to manage and customize folder configurations
 */
export class ChatFoldersManager {
    constructor(customConfigs = []) {
        this.configs = [...DEFAULT_FOLDER_CONFIGS, ...customConfigs];
        this.sortConfigs();
    }
    
    /**
     * Sort configurations by order
     */
    sortConfigs() {
        this.configs.sort((a, b) => (a.order || 999) - (b.order || 999));
    }
    
    /**
     * Get all folder configurations
     */
    getFolderConfigs() {
        return [...this.configs];
    }
    
    /**
     * Get enabled folder configurations only
     */
    getEnabledFolderConfigs() {
        return this.configs.filter(config => config.enabled !== false);
    }
    
    /**
     * Add a new folder configuration
     */
    addFolder(config) {
        if (!config.id) throw new Error('Folder config must have an id');
        if (!config.title) throw new Error('Folder config must have a title');
        if (!config.apiRequestBuilder || typeof config.apiRequestBuilder !== 'function') {
            throw new Error('Folder config must have an apiRequestBuilder function');
        }
        
        const existingIndex = this.configs.findIndex(c => c.id === config.id);
        if (existingIndex >= 0) {
            this.configs[existingIndex] = { ...this.configs[existingIndex], ...config };
        } else {
            this.configs.push(config);
        }
        
        this.sortConfigs();
        return this;
    }
    
    /**
     * Enable or disable a folder
     */
    toggleFolder(folderId, enabled) {
        const folder = this.configs.find(c => c.id === folderId);
        if (folder) {
            folder.enabled = enabled;
        }
        return this;
    }
    
    /**
     * Remove a folder configuration
     */
    removeFolder(folderId) {
        this.configs = this.configs.filter(c => c.id !== folderId);
        return this;
    }
    
    /**
     * Update folder configuration
     */
    updateFolder(folderId, updates) {
        const folder = this.configs.find(c => c.id === folderId);
        if (folder) {
            Object.assign(folder, updates);
            this.sortConfigs();
        }
        return this;
    }
    
    /**
     * Create a folder for specific tags (convenience method)
     */
    addTagFolder(folderId, title, tags, options = {}) {
        return this.addFolder({
            id: folderId,
            title,
            apiRequestBuilder: (store) => {
                const allTags = store?.getters['tags/allTags'] || [];
                const tagNames = Array.isArray(tags) ? tags : [tags];
                const matchingTags = allTags.filter(tag => 
                    tag.name && tagNames.some(tagName => 
                        tag.name.toLowerCase() === tagName.toLowerCase()
                    )
                );

                if (matchingTags.length === 0) {
                    return { filters: null, params: {} };
                }

                const tagUuid = matchingTags[0].uuid;
                return {
                    filters: { tag: [tagUuid] },
                    params: { 'filters.tag': tagUuid }
                };
            },
            svgIcon: 'tag_icon',
            enabled: true,
            defaultExpanded: false,
            order: 10,
            ...options
        });
    }
}

/**
 * Default instance for easy usage
 */
export const defaultChatFoldersManager = new ChatFoldersManager();

/**
 * Convenience function to get default folder configs
 */
export const getChatFolderConfigs = () => defaultChatFoldersManager.getFolderConfigs();